import { Info } from "lucide-react";

import { Eyebrow } from "@/components/eyebrow";
import { MarkdownArticle } from "@/components/markdown-article";
import { PageShell } from "@/components/page-shell";
import { Card, CardContent } from "@/components/ui/card";
import { PRIVACY_POLICY_MARKDOWN } from "@/features/legal/privacy-policy-content";

/**
 * Public privacy policy. Deliberately not noindex'd: people must be able to
 * read it before creating an account, and search engines should find it.
 *
 * The notice sits *below* the policy, as a footnote. It used to be a red alert
 * above the text, which was right when the document was full of blanks and
 * wrong now that it isn't: every claim in it describes what the code actually
 * does. What remains true, and must stay stated, is that no lawyer has read it.
 * Do not remove the notice; remove it only once one actually has.
 */
export function PrivacyPolicyPage() {
  return (
    // `wide` (max-w-5xl) rather than the default max-w-3xl: the policy carries
    // four tables (sub-processors, lawful bases, cookies, retention) and they
    // are unreadable squeezed into a prose measure.
    <PageShell wide>
      <Eyebrow>Legal</Eyebrow>
      <h1 className="text-primary mt-3 text-4xl font-normal tracking-tight sm:text-5xl">
        Privacy policy.
      </h1>

      <Card className="mt-6">
        <CardContent className="pt-6">
          {/* `MarkdownArticle` defaults to text-sm, which suits a scannable report
              body. A privacy policy is read start-to-finish, so it gets body-copy
              size and a looser measure. `text-base` also resets the line-height
              that `leading-relaxed` set on the smaller type, hence `leading-7`.

              No max-w on the prose: the `wide` shell already caps the card at
              max-w-5xl, which measures ~926px, under 90 characters at this size,
              so an extra measure cap would be dead styling. */}
          <MarkdownArticle
            markdown={PRIVACY_POLICY_MARKDOWN}
            className="text-base leading-7 [&_li]:my-1.5 [&_p]:my-4"
          />
        </CardContent>
      </Card>

      <div className="text-muted-foreground mt-6 flex gap-3 text-sm">
        <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <p>
          <span className="text-primary font-medium">Early access: not reviewed by a lawyer.</span>{" "}
          This policy accompanies an early-access release. It describes what the tool actually does
          today, and where we could not establish a fact we left it out rather than guess. If
          anything here is unclear or looks wrong, tell us at{" "}
          <a className="underline" href="mailto:hello@boardofinnovation.com">
            hello@boardofinnovation.com
          </a>
          .
        </p>
      </div>
    </PageShell>
  );
}
